import type { IPickupDetails, Question } from '@common-types';

/** Presence of any of these booking questions enables the matching arrival mode option. */
const TRANSFER_ARRIVAL_MODE_MARKERS: Record<string, readonly string[]> = {
    AIR: ['TRANSFER_AIR_ARRIVAL_AIRLINE', 'TRANSFER_AIR_ARRIVAL_FLIGHT_NO'],
    /** `TRANSFER_PORT_CRUISE_SHIP` applies to arrival and departure (SEA). */
    SEA: ['TRANSFER_PORT_ARRIVAL_TIME', 'TRANSFER_PORT_CRUISE_SHIP'],
    RAIL: ['TRANSFER_RAIL_ARRIVAL_LINE', 'TRANSFER_RAIL_ARRIVAL_STATION'],
};

/** Presence of any of these booking questions enables the matching departure mode option. */
const TRANSFER_DEPARTURE_MODE_MARKERS: Record<string, readonly string[]> = {
    AIR: ['TRANSFER_AIR_DEPARTURE_AIRLINE', 'TRANSFER_AIR_DEPARTURE_FLIGHT_NO'],
    SEA: ['TRANSFER_PORT_DEPARTURE_TIME', 'TRANSFER_PORT_CRUISE_SHIP'],
    RAIL: ['TRANSFER_RAIL_DEPARTURE_LINE', 'TRANSFER_RAIL_DEPARTURE_STATION'],
};

export function getBookingQuestionCodesUpper(questions: Question[] | undefined | null): Set<string> {
    const set = new Set<string>();
    questions?.forEach((q) => {
        if (q.code) set.add(String(q.code).toUpperCase());
        if ((q as { id?: string }).id) set.add(String((q as { id?: string }).id).toUpperCase());
    });
    return set;
}

function hasAnyQuestionMarker(questionCodes: Set<string>, markers: readonly string[]): boolean {
    return markers.some((m) => questionCodes.has(m.toUpperCase()));
}

export function isTransferArrivalModeOptionApplicable(optionCode: string, questionCodes: Set<string>): boolean {
    const o = (optionCode || '').toUpperCase();
    if (o === 'OTHER') {
        return true;
    }
    const markers = TRANSFER_ARRIVAL_MODE_MARKERS[o];
    if (!markers) {
        return true;
    }
    return hasAnyQuestionMarker(questionCodes, markers);
}

export function isTransferDepartureModeOptionApplicable(optionCode: string, questionCodes: Set<string>): boolean {
    const o = (optionCode || '').toUpperCase();
    if (o === 'OTHER') {
        return true;
    }
    const markers = TRANSFER_DEPARTURE_MODE_MARKERS[o];
    if (!markers) {
        return true;
    }
    return hasAnyQuestionMarker(questionCodes, markers);
}

/**
 * Filters AIR / RAIL / SEA options for transfer mode questions when the product only returns
 * follow-up questions for some modes. OTHER is kept whenever the API includes it.
 * If every option would be removed, returns the original list (legacy fallback).
 */
export function filterTransferModeQuestionOptions(
    questionCode: string | undefined,
    options: { code: string; text: string }[] | undefined,
    detailQuestions: Question[] | undefined | null
): { code: string; text: string }[] {
    if (!options?.length) return [];
    const qc = (questionCode || '').toUpperCase();
    if (qc !== 'TRANSFER_ARRIVAL_MODE' && qc !== 'TRANSFER_DEPARTURE_MODE') {
        return options;
    }
    const codes = getBookingQuestionCodesUpper(detailQuestions ?? undefined);
    const filtered = options.filter((opt) => {
        const o = (opt.code || '').toUpperCase();
        if (qc === 'TRANSFER_ARRIVAL_MODE') {
            return isTransferArrivalModeOptionApplicable(o, codes);
        }
        return isTransferDepartureModeOptionApplicable(o, codes);
    });
    return filtered.length > 0 ? filtered : options;
}

export interface QuestionMetaForOptions {
    id: string;
    /**
     * If non-empty, restrict to these option codes (intersected with the option list).
     * If missing or empty, use the full options from the questions call, then the session product question.
     */
    allowedAnswers?: string[];
    /** From getQuestions — full { code, text } list when the API provides it. */
    options?: { code: string; text: string }[];
}

export function findQuestionMeta(
    questionMetaList: QuestionMetaForOptions[] | undefined | null,
    code: string | undefined
): QuestionMetaForOptions | undefined {
    if (!questionMetaList?.length || !code) {
        return undefined;
    }
    const c = String(code);
    return questionMetaList.find((iq) => String(iq.id) === c || String(iq.id).toUpperCase() === c.toUpperCase());
}

/**
 * Resolves { code, text } options from a getQuestions row. Handles `options`, common alternate
 * array keys, or `allowedAnswers` as a plain code list when that is the only data returned.
 */
export function extractOptionsFromQuestionMeta(
    meta: (QuestionMetaForOptions & Record<string, unknown>) | null | undefined
): { code: string; text: string }[] {
    if (!meta) {
        return [];
    }
    const raw = meta.options;
    if (Array.isArray(raw) && raw.length > 0) {
        return (raw as { code?: string; text?: string; value?: string; label?: string; name?: string }[])
            .map((row) => ({
                code: String(row?.code ?? row?.value ?? '').trim(),
                text: String(row?.text ?? row?.label ?? row?.name ?? row?.code ?? row?.value ?? '').trim() || String(row?.code ?? row?.value ?? ''),
            }))
            .filter((o) => o.code);
    }
    const alt = meta.optionList ?? meta.answers ?? (meta as { questionOptions?: unknown }).questionOptions;
    if (Array.isArray(alt) && alt.length > 0) {
        return (alt as { code?: string; text?: string; value?: string; label?: string; id?: string; name?: string }[])
            .map((row) => ({
                code: String(row?.code ?? row?.value ?? row?.id ?? '').trim(),
                text: String(row?.text ?? row?.label ?? row?.name ?? row?.code ?? row?.value ?? '').trim() || String(row?.code ?? row?.value ?? row?.id ?? ''),
            }))
            .filter((o) => o.code);
    }
    if (meta.allowedAnswers && meta.allowedAnswers.length > 0) {
        return meta.allowedAnswers.map((a) => {
            const code = String(a).trim();
            return { code, text: code };
        });
    }
    return [];
}

/**
 * For TRANSFER_ARRIVAL_MODE / TRANSFER_DEPARTURE_MODE: merge session `detailLabel` options with
 * the getQuestions (`questionMetaList`) entry. If `allowedAnswers` is non-empty, narrow to those codes
 * (intersection). If it is missing or empty, use the full option list: prefer the session product
 * question, then `meta.options` from the questions call, then marker filtering as usual.
 */
export function getTransferModeResolvedOptions(
    q: Question,
    detailQuestions: Question[] | undefined | null,
    questionMetaList: QuestionMetaForOptions[] | undefined | null
): { code: string; text: string }[] {
    const qc = (q.code || '').toUpperCase();
    if (qc !== 'TRANSFER_ARRIVAL_MODE' && qc !== 'TRANSFER_DEPARTURE_MODE') {
        return (q.options || []) as { code: string; text: string }[];
    }
    const fromSession = ((q.options || []) as { code: string; text: string }[]).filter(Boolean);
    const meta = findQuestionMeta(questionMetaList, q.code);
    const fromQuestionsCall = extractOptionsFromQuestionMeta(
        meta as (QuestionMetaForOptions & Record<string, unknown>) | undefined
    );
    const hasAllowList = Boolean(meta?.allowedAnswers && meta.allowedAnswers.length > 0);

    // No allowList: use full options from getQuestions when present, else the session (detail) question.
    let base: { code: string; text: string }[] = hasAllowList
        ? fromSession.length > 0
            ? fromSession
            : fromQuestionsCall
        : fromQuestionsCall.length > 0
          ? fromQuestionsCall
          : fromSession;

    if (base.length === 0 && hasAllowList && meta?.allowedAnswers?.length) {
        base = meta.allowedAnswers.map((a) => {
            const code = String(a);
            return { code, text: code };
        });
    }

    if (hasAllowList && meta?.allowedAnswers) {
        const allow = new Set(meta.allowedAnswers.map((a) => String(a).toUpperCase()));
        const tryNarrow = (candidates: { code: string; text: string }[]) =>
            candidates.filter((o) => allow.has(String(o.code).toUpperCase()));
        let narrowed = tryNarrow(base);
        if (narrowed.length === 0) {
            narrowed = tryNarrow(fromQuestionsCall);
        }
        if (narrowed.length === 0) {
            narrowed = tryNarrow(fromSession);
        }
        if (narrowed.length > 0) {
            base = narrowed;
        }
    }

    const filtered = filterTransferModeQuestionOptions(q.code, base, detailQuestions ?? undefined);
    if (filtered.length > 0) {
        return filtered;
    }
    const fromDef = fromQuestionsCall.length > 0 ? fromQuestionsCall : fromSession;
    const second = filterTransferModeQuestionOptions(q.code, fromDef, detailQuestions ?? undefined);
    if (second.length > 0) {
        return second;
    }
    const fallback: { code: string; text: string }[] = [
        { code: 'AIR', text: 'AIR' },
        { code: 'RAIL', text: 'RAIL' },
        { code: 'SEA', text: 'SEA' },
        { code: 'OTHER', text: 'OTHER' },
    ];
    return filterTransferModeQuestionOptions(q.code, fallback, detailQuestions ?? undefined);
}

export function isTransferModeAnswerApplicable(
    questionCode: string | undefined,
    selectedValue: unknown,
    detailQuestions: Question[] | undefined | null,
    questionMetaList?: QuestionMetaForOptions[] | null
): boolean {
    if (selectedValue === undefined || selectedValue === null) {
        return false;
    }
    const s = String(selectedValue).trim();
    if (!s) {
        return false;
    }
    const qc = (questionCode || '').toUpperCase();
    if (qc !== 'TRANSFER_ARRIVAL_MODE' && qc !== 'TRANSFER_DEPARTURE_MODE') {
        return true;
    }
    const q = detailQuestions?.find((x) => x.code === questionCode || (x as { id?: string }).id === questionCode);
    if (!q) {
        return false;
    }
    const resolved = getTransferModeResolvedOptions(q, detailQuestions, questionMetaList);
    if (resolved.length === 0) {
        return true;
    }
    const allowed = new Set(resolved.map((o) => String(o.code).toUpperCase()));
    return allowed.has(s.toUpperCase());
}

export function normPickupType(p: IPickupDetails): string {
    return (p.pickupType || '').toUpperCase();
}

/** Shown in checkout "other" pickup radios even when TRANSFER_ARRIVAL_MODE has no OTHER option. */
export function isExemptFromTransferArrivalOtherGate(reference: string | undefined): boolean {
    if (!reference) {
        return false;
    }
    const u = String(reference).toUpperCase();
    return u === 'CONTACT_SUPPLIER_LATER' || u === 'MEET_AT_DEPARTURE_POINT';
}

function hasPickupTypeIn(pickups: IPickupDetails[], type: string): boolean {
    const t = type.toUpperCase();
    return pickups.some(p => normPickupType(p) === t);
}

export function findTransferArrivalModeQuestion(questions: Question[] | undefined): Question | undefined {
    return questions?.find(q => q.code === 'TRANSFER_ARRIVAL_MODE');
}

export function transferArrivalModeHasOtherOption(question: Question | undefined): boolean {
    return Boolean(question?.options?.some(o => (o.code || '').toUpperCase() === 'OTHER'));
}

/**
 * When TRANSFER_ARRIVAL_MODE is answered, optionally restrict **dropdown** options (non-OTHER rows only).
 * Returns null = legacy list (all non-OTHER pickups). OTHER-type options stay on their existing radios.
 */
export function getAllowedPickupTypesForSelect(
    transferAnswer: string | undefined,
    allowCustomTravelerPickup: boolean | undefined,
    travelerPickups: IPickupDetails[]
): string[] | null {
    const ans = (transferAnswer || '').toUpperCase();
    const allowCustom = allowCustomTravelerPickup === true;

    if (ans === 'AIR') {
        const applies = allowCustom || hasPickupTypeIn(travelerPickups, 'AIRPORT');
        if (!applies) {
            return null;
        }
        return ['AIRPORT'];
    }
    if (ans === 'SEA') {
        const applies = allowCustom || hasPickupTypeIn(travelerPickups, 'PORT');
        if (!applies) {
            return null;
        }
        return ['PORT'];
    }
    if (ans === 'OTHER') {
        const applies =
            allowCustom ||
            hasPickupTypeIn(travelerPickups, 'HOTEL') ||
            hasPickupTypeIn(travelerPickups, 'LOCATION') ||
            hasPickupTypeIn(travelerPickups, 'OTHER');
        if (!applies) {
            return null;
        }
        // Dropdown: HOTEL + LOCATION only; pickupType OTHER stays on separate radios (unchanged UX).
        return ['HOTEL', 'LOCATION'];
    }
    return null;
}

export function filterPickupsForLocationSelect(
    travelerPickups: IPickupDetails[],
    allowedTypes: string[] | null
): IPickupDetails[] {
    const nonOther = travelerPickups.filter(p => normPickupType(p) !== 'OTHER');
    if (!allowedTypes || allowedTypes.length === 0) {
        return nonOther;
    }
    const set = new Set(allowedTypes.map(t => t.toUpperCase()));
    return nonOther.filter(p => set.has(normPickupType(p)));
}

export function filterOtherPickupRadios(
    travelerPickups: IPickupDetails[],
    transferArrivalQuestion: Question | undefined
): IPickupDetails[] {
    return travelerPickups.filter(p => {
        if (isExemptFromTransferArrivalOtherGate(p.reference)) {
            return true;
        }
        if (normPickupType(p) !== 'OTHER') {
            return false;
        }
        if (!transferArrivalQuestion) {
            return true;
        }
        return transferArrivalModeHasOtherOption(transferArrivalQuestion);
    });
}

export function isPickupReferenceAllowed(
    reference: string | undefined,
    travelerPickups: IPickupDetails[],
    allowedTypes: string[] | null,
    transferArrivalQuestion: Question | undefined
): boolean {
    if (!reference) {
        return true;
    }
    const p = travelerPickups.find(x => x.reference === reference);
    if (!p) {
        return false;
    }
    if (isExemptFromTransferArrivalOtherGate(p.reference)) {
        return true;
    }
    const t = normPickupType(p);
    if (t === 'OTHER') {
        return !transferArrivalQuestion || transferArrivalModeHasOtherOption(transferArrivalQuestion);
    }
    if (!allowedTypes || allowedTypes.length === 0) {
        return true;
    }
    return new Set(allowedTypes.map(x => x.toUpperCase())).has(t);
}
